import React, { useEffect, useState } from 'react';
import { Check, ArrowRight, X } from 'lucide-react';
import { pricingPlans as mockPlans } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { fetchPricing, createBooking } from '../api/easyworks';
import { validateBooking, hasErrors } from '../lib/validators';

const BookingDialog = ({ plan, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateBooking(form);
    if (hasErrors(found)) {
      setErrors(found);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await createBooking({
        planId: plan.id,
        name: form.name,
        phone: form.phone,
        email: form.email,
        company: form.company,
      });
      onSuccess(plan);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.';
      toast({ title: 'Booking failed', description: typeof msg === 'string' ? msg : 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-black/5 p-6 md:p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500">
          <X size={18} />
        </button>
        <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold">{plan.name}</div>
        <div className="text-2xl font-extrabold text-neutral-900 mt-1">
          ₹ {plan.price}<span className="text-sm text-neutral-500 font-medium">/{plan.period || 'Month'}</span>
        </div>
        <p className="mt-1 text-sm text-neutral-600">Share a few details and our team will get in touch within 24 hours to finalise your plan.</p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <input
              value={form.name}
              onChange={(e)=>update('name', e.target.value)}
              aria-invalid={!!errors.name}
              data-testid="booking-input-name"
              className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
              placeholder="Your name *"
            />
            {errors.name && <p data-testid="booking-error-name" className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>
          <div>
            <input
              value={form.phone}
              onChange={(e)=>update('phone', e.target.value)}
              aria-invalid={!!errors.phone}
              data-testid="booking-input-phone"
              className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.phone ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
              placeholder="Phone number *"
            />
            {errors.phone && <p data-testid="booking-error-phone" className="mt-1 text-xs text-red-600">{errors.phone}</p>}
          </div>
          <div>
            <input
              value={form.email}
              onChange={(e)=>update('email', e.target.value)}
              aria-invalid={!!errors.email}
              data-testid="booking-input-email"
              className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
              placeholder="Email (optional)"
            />
            {errors.email && <p data-testid="booking-error-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>
          <input value={form.company} onChange={(e)=>update('company', e.target.value)} data-testid="booking-input-company" className="w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border border-transparent focus:border-[#F39200] focus:bg-white outline-none text-sm" placeholder="Company (optional)" />
          <button type="submit" disabled={loading} data-testid="booking-submit-btn" className="w-full h-11 rounded-lg bg-neutral-900 hover:bg-neutral-800 disabled:opacity-60 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
            {loading ? 'Booking...' : (<>Confirm Booking <ArrowRight size={16} /></>)}
          </button>
        </form>
      </div>
    </div>
  );
};

const Pricing = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchPricing();
        if (!mounted) return;
        if (Array.isArray(data) && data.length) setPlans(data);
        else setPlans(mockPlans.map((p, i) => ({ ...p, id: `mock-${i}`, period: 'Month' })));
      } catch (e) {
        if (!mounted) return;
        setPlans(mockPlans.map((p, i) => ({ ...p, id: `mock-${i}`, period: 'Month' })));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onBooked = (plan) => {
    setSelected(null);
    toast({ title: 'Booking confirmed!', description: `Your ${plan.name} booking is received. We will call you shortly.` });
  };

  return (
    <section className="bg-[#FAF2E8] py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900">
          Decide your office{' '}
          <span className="relative inline-block text-[#F39200]">pricing
            <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" fill="none">
              <path d="M2 7 C 60 1, 130 1, 198 6" stroke="#F39200" strokeWidth="2" strokeLinecap="round" fill="none" />
            </svg>
          </span>
        </h2>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {loading && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white border border-black/5 p-7 animate-pulse h-96" />
          ))}
          {!loading && plans.map((p) => (
            <div
              key={p.id}
              className={`relative rounded-2xl border bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-xl ${p.highlight ? 'border-[#F39200] shadow-lg' : 'border-black/5 shadow-sm'}`}
            >
              {p.highlight && (
                <span className="absolute -top-3 right-6 bg-[#F39200] text-white text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wide">Most Popular</span>
              )}
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-neutral-900">₹ {p.price}</span>
                <span className="text-sm text-neutral-500">/{p.period || 'Month'}</span>
              </div>
              <button
                onClick={() => setSelected(p)}
                className={`mt-5 w-full h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${p.highlight ? 'bg-[#F39200] hover:bg-[#E08400] text-white' : 'bg-neutral-900 hover:bg-neutral-800 text-white'}`}
              >
                Get Started <ArrowRight size={16} />
              </button>
              <div className="mt-6 pt-5 border-t border-neutral-100">
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-3">Service Benefits</div>
                <ul className="space-y-2.5">
                  {p.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-neutral-700">
                      <Check size={16} className="text-[#F39200] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <BookingDialog plan={selected} onClose={() => setSelected(null)} onSuccess={onBooked} />
      )}
    </section>
  );
};

export default Pricing;
